/**
 * Configuration for routes that should display full-width layout
 * Routes in this array will have no padding and a gray background
 */

interface FullWidthRoute {
  module: string;
  submodule?: string;
  tab?: string;
}

export interface FullWidthRouteParams {
  module: string | null;
  submodule: string | null;
  tab: string | null;
  pathname: string;
  featureFlags?: {
    jobsErpDetailsPageEnabled?: boolean;
  };
}

export const FULL_WIDTH_ROUTES: FullWidthRoute[] = [
  // Dashboard module
  { module: 'dashboard' },

  // RaptorJob module
  { module: 'raptorjob' },

  // Certperm module
  { module: 'certperm' },

  // Sales module
  { module: 'sales' },

  // Quotes module
  { module: 'quotes' },

  // Jobs module (all submodules)
  { module: 'jobs' },

  // Equipment module (full width for all submodules/tabs)
  { module: 'equipment' },

  // Employees module (full width for all submodules/tabs)
  { module: 'employees' },

  // User Management (module page brings its own header/tabs; avoid double padding from Layout Paper)
  { module: 'user-management' }
];

/**
 * Check if current route should display full-width layout
 * @param params - Route parameters object
 * @returns true if route should be full-width
 */
export const isFullWidthRoute = ({ module, submodule, tab, pathname, featureFlags }: FullWidthRouteParams): boolean => {
  // Home page is always full-width
  if (pathname === '/') {
    return true;
  }

  if (pathname.startsWith('/quotes')) {
    return true;
  }

  if (pathname.startsWith('/user-management')) {
    return true;
  }

  // Check against configured full-width routes
  const matchesStaticRoute = FULL_WIDTH_ROUTES.some((route) => {
    // If submodule is specified in config, both module and submodule must match
    if (route.submodule) {
      // If tab is also specified, all three must match
      if (route.tab) {
        return module === route.module && submodule === route.submodule && tab === route.tab;
      }
      return module === route.module && submodule === route.submodule;
    }

    // If only module is specified, just match the module
    return module === route.module;
  });

  if (matchesStaticRoute) {
    return true;
  }

  // Check conditional routes based on feature flags
  // ERP Job Details page (jobs > dispatch > information) - only full-width if feature flag is enabled
  if (featureFlags?.jobsErpDetailsPageEnabled && module === 'jobs' && submodule === 'dispatch' && tab === 'information') {
    return true;
  }

  return false;
};
